import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import usePermission from '../Hooks/usePermission';
import api from "../helpers/api";
import Select from "react-select";
import { formatDateTimeForAPI } from "../constants/utils";
import AssetUsageStatusFields from "./AssetUsageStatusFields";
import { Row, Col, Input, Spinner, Modal } from "reactstrap";
import { motion } from "framer-motion";
import FeatherIcon from "feather-icons-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import RecurrenceSelector from "./RecurrenceSelector";
import WorkOrderAssignToPicker from "./WorkOrderAssignToPicker";

/**
 * Shared WorkOrderForm component extracted from Reactive.jsx.
 * Renders all work-order form fields. Does NOT handle submit logic,
 * modal wrapping, or validation — the parent does those.
 *
 * Props:
 *   formData          — object with all WO field values
 *   onChange(updates)  — callback receiving partial updates, e.g. { title: 'new' }
 *   errors             — validation errors object
 *   apiError           — API error string
 *   locations          — location options array
 *   technicians        — technician options array
 *   categories         — category options array
 *   vendors            — vendor options array
 *   enumData           — dynamic enums array
 *   isEditMode         — boolean, show Status field
 *   organizationId     — for asset search API
 *   manufacturers      — optional, for asset filter modal
 */
const WorkOrderForm = ({
  formData,
  onChange,
  errors = {},
  apiError,
  locations,
  technicians,
  teams = [],
  categories,
  vendors,
  enumData,
  isEditMode,
  organizationId,
  manufacturers = [],
}) => {
  const { t } = useTranslation();
  const { visibilityRestricted } = usePermission();
  // Internal state for asset search
  const [assetSearchTerm, setAssetSearchTerm] = useState("");
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [selectedAssetDisplay, setSelectedAssetDisplay] = useState("");
  const [assets, setAssets] = useState([]);
  const [assetFilterModal, setAssetFilterModal] = useState(false);
  const [assetFilters, setAssetFilters] = useState({
    location: '',
    manufacturer: ''
  });
  const [imageUploading, setImageUploading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);

  // Fetch assets on search/filter change
  const fetchAssets = useCallback(async () => {
    try {
      const params = {
        organizationId,
        count: 5,
        search: assetSearchTerm,
        currentLocation: assetFilters.location,
        manufacturer: assetFilters.manufacturer,
        hierarchy: false
      };
      const { data } = await api.get(`asset/all`, { params });
      setAssets(data?.response[0]?.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  }, [assetSearchTerm, assetFilters, organizationId]);

  useEffect(() => {
    if (assetSearchTerm.length > 0) {
      const debounce = setTimeout(fetchAssets, 300);
      return () => clearTimeout(debounce);
    }
  }, [assetSearchTerm, fetchAssets]);

  useEffect(() => {
    if (assetFilters.location || assetFilters.manufacturer) {
      fetchAssets();
    }
  }, [assetFilters, fetchAssets]);


  // Sync selected asset display from formData
  useEffect(() => {
    if (formData?.asset && formData.asset.id) {
      const a = formData.asset;
      setSelectedAssetDisplay(`${a.assetName}${a.assetNumber ? ` - ID: ${a.assetNumber}` : ''} - ${a.serialNumber || ''} (${a.currentLocation || ''})`);
    } else if (formData?.assetId && !formData?.asset) {
      // An assetId with no asset object means the server did not return the asset. For a
      // restricted user that is the normal signal that the linked asset sits outside their
      // team scope, so say that rather than showing a bare id.
      setSelectedAssetDisplay(
        visibilityRestricted
          ? t('teams.restrictVisibility.assetOutOfScopeShort', 'Asset outside your scope')
          : t('business.components.work_order_form.asset_id_display', 'Asset ID: {{id}}', { id: formData.assetId }),
      );
    } else {
      setSelectedAssetDisplay("");
    }
  }, [formData?.asset, formData?.assetId, visibilityRestricted, t]);

  const handleAssetSelect = (asset) => {
    const locationId = asset.currentLocation
      ? locations?.find(loc => loc.locationName === asset.currentLocation)?.id
      : null;

    onChange({
      asset: asset,
      assetId: asset.id,
      locationId: locationId || formData?.locationId || '',
      usageStatus: asset.assetStatus || '',
    });
    setSelectedAssetDisplay(`${asset.assetName}${asset.assetNumber ? ` - ID: ${asset.assetNumber}` : ''} - ${asset.serialNumber} (${asset.currentLocation})`);
    setAssetSearchTerm("");
    setShowAssetDropdown(false);
  };

  const handleAssetSearchChange = (e) => {
    const value = e.target.value;
    setAssetSearchTerm(value);
    setShowAssetDropdown(value.length > 0);
    if (value.length === 0) {
      onChange({ asset: null, assetId: '', usageStatus: '' });
      setSelectedAssetDisplay("");
    }
  };

  // Select style helper
  const selectStyles = (hasError) => ({
    control: (base, state) => ({
      ...base,
      borderColor: hasError ? '#dc3545' : (state.isFocused ? '#86b7fe' : '#ced4da'),
      boxShadow: hasError
        ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
        : (state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none'),
      '&:hover': { borderColor: '#86b7fe' }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3980c0' : state.isFocused ? '#3980c0' : 'white',
      color: state.isSelected || state.isFocused ? 'white' : '#212529',
      '&:active': { backgroundColor: '#2e669a' }
    })
  });

  const multiSelectStyles = (hasError) => ({
    ...selectStyles(hasError),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#3980c0',
      borderRadius: '4px'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: 'white',
      padding: '2px 6px'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: 'white',
      '&:hover': {
        backgroundColor: '#2e669a',
        color: 'white'
      }
    })
  });

  return (
    <>
      <form>
        {/* Title Field */}
        <Row className="mb-4">
          <Col md={12}>
            <div className="form-group">
              <label htmlFor="title" className="form-label fw-medium">
                {t('shared.title', 'Title')} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="title"
                className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                value={formData?.title || ''}
                onChange={(e) => onChange({ title: e.target.value })}
                placeholder={t('shared.issue_title_placeholder', 'Brief description of the issue')}
              />
              {errors.title && <small className="text-danger">{errors.title}</small>}
            </div>
          </Col>
        </Row>

        {/* Asset Selection */}
        <Row className="mb-4">
          <Col md={12}>
            <div className="form-group">
              <label htmlFor="assetSearch" className="form-label fw-medium">
                {t('shared.select_asset', 'Select Asset')}
              </label>
              <div className="d-flex gap-2">
                <div className="flex-grow-1 position-relative">
                  {selectedAssetDisplay ? (
                    <div className="d-flex">
                      <div
                        className="form-control d-flex align-items-center justify-content-between cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedAssetDisplay("");
                          onChange({ asset: null, assetId: '', usageStatus: '' });
                        }}
                      >
                        <span>{selectedAssetDisplay}</span>
                        <FeatherIcon icon="x" size={16} className="text-muted" />
                      </div>
                    </div>
                  ) : (
                    <>
                      <Input
                        type="text"
                        id="assetSearch"
                        placeholder={t('shared.type_to_search_assets', 'Type to search assets...')}
                        value={assetSearchTerm}
                        onChange={handleAssetSearchChange}
                        onFocus={() => {
                          if (assetSearchTerm.length > 0) {
                            setShowAssetDropdown(true);
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowAssetDropdown(false), 200);
                        }}
                        className={errors.assetId ? 'is-invalid' : ''}
                      />
                      {showAssetDropdown && assets.length > 0 && (
                        <div
                          className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm"
                          style={{ zIndex: 1050, maxHeight: '200px', overflowY: 'auto', top: '100%' }}
                        >
                          {assets.map((asset) => (
                            <div
                              key={asset.id}
                              className="p-2 border-bottom cursor-pointer hover-bg-light d-flex align-items-center"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleAssetSelect(asset)}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                            >
                              <div className="flex-grow-1">
                                <div className="fw-medium text-dark">{asset.assetName}{asset.assetNumber && <span className="text-muted"> (ID: {asset.assetNumber})</span>}</div>
                                <small className="text-muted">
                                  {asset.serialNumber} • {asset.currentLocation}
                                </small>
                              </div>
                              <FeatherIcon icon="chevron-right" size={16} className="text-muted" />
                            </div>
                          ))}
                        </div>
                      )}
                      {showAssetDropdown && assetSearchTerm.length > 0 && assets.length === 0 && (
                        <div
                          className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm p-3 text-center text-muted"
                          style={{ zIndex: 1050, top: '100%' }}
                        >
                          {t('shared.no_assets_found_matching', 'No assets found matching "{{searchTerm}}"', { searchTerm: assetSearchTerm })}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="align-self-end">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => setAssetFilterModal(true)}
                    title={t('shared.filter_assets', 'Filter Assets')}
                    style={{ height: '38px' }}
                  >
                    <FeatherIcon icon="filter" size={16} />
                  </button>
                </div>
              </div>
              {errors.assetId && <small className="text-danger">{errors.assetId}</small>}
            </div>
          </Col>
        </Row>

        {/* Asset Usage Status + Downtime Reason */}
        {formData?.assetId && (
          <AssetUsageStatusFields
            usageStatus={formData?.usageStatus}
            downtimeReasonId={formData?.downtimeReasonId}
            onChange={(updates) => onChange(updates)}
            errors={errors}
            variant="react-select"
            layout="stacked"
            selectStyles={selectStyles}
          />
        )}

        {/* Location Dropdown */}
        <Row className="mb-4">
          <Col md={12}>
            <div className="form-group">
              <label htmlFor="locationId" className="form-label fw-medium">
                {t('shared.location', 'Location')}
              </label>
              <Select
                id="locationId"
                options={locations?.map((location) => ({
                  value: location.id,
                  label: location.locationName
                })) || []}
                value={
                  locations?.find(loc => loc.id === formData?.locationId)
                    ? { value: formData.locationId, label: locations.find(loc => loc.id === formData.locationId)?.locationName }
                    : null
                }
                onChange={(selected) => onChange({ locationId: selected?.value || '' })}
                isClearable
                isSearchable
                placeholder={t('shared.search_and_select_location', 'Search and select location...')}
                classNamePrefix="react-select"
                styles={selectStyles(false)}
              />
            </div>
          </Col>
        </Row>

        {/* Image Upload */}
        <Row className="mb-4">
          <Col md={12}>
            <div className="form-group">
              <label className="form-label fw-medium">{t('shared.images', 'Images')}</label>
              <div
                className="border rounded p-4 text-center"
                style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: '#dee2e6', backgroundColor: '#fafbfc' }}
              >
                <input
                  type="file"
                  id="imageUploadInput"
                  className="d-none"
                  accept="image/*"
                  multiple
                  onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    if (files.length > 0) {
                      setImageUploading(true);
                      try {
                        const fd = new FormData();
                        files.forEach(file => fd.append('files', file));
                        const { data } = await api.post('files/img-upload', fd, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        const newUrls = (data.response || []).map(item => item.publicUrl);
                        onChange({ imageUrls: [...(formData?.imageUrls || []), ...newUrls] });
                      } catch (error) {
                        console.error('Error uploading images:', error);
                      } finally {
                        setImageUploading(false);
                      }
                    }
                  }}
                  disabled={imageUploading}
                />
                <label
                  htmlFor="imageUploadInput"
                  className={`btn btn-outline-primary mb-2 ${imageUploading ? 'disabled' : ''}`}
                  style={{ cursor: imageUploading ? 'not-allowed' : 'pointer' }}
                >
                  {imageUploading ? (
                    <><Spinner size="sm" className="me-2" />{t('shared.uploading', 'Uploading...')}</>
                  ) : (
                    <><FeatherIcon icon="upload" size={16} className="me-2" />{t('shared.upload_images', 'Upload Images')}</>
                  )}
                </label>
                <p className="text-muted small mb-0">
                  {imageUploading ? t('shared.wait_uploading_images', 'Please wait while images are being uploaded...') : t('shared.click_to_select_images', 'Click to select images to upload')}
                </p>
              </div>
              {formData?.imageUrls?.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-3">
                  {formData.imageUrls.map((url, index) => (
                    <div key={index} className="position-relative rounded overflow-hidden" style={{ width: '100px', height: '100px' }}>
                      <img src={url} alt={`Upload ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm position-absolute d-flex align-items-center justify-content-center"
                        style={{ top: '4px', right: '4px', width: '24px', height: '24px', padding: 0, borderRadius: '50%' }}
                        onClick={() => {
                          const newUrls = formData.imageUrls.filter((_, i) => i !== index);
                          onChange({ imageUrls: newUrls });
                        }}
                      >
                        <FeatherIcon icon="x" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Description */}
        <Row className="mb-4">
          <Col md={12}>
            <div className="form-group">
              <label htmlFor="description" className="form-label fw-medium">{t('shared.description', 'Description')}</label>
              <textarea
                id="description"
                className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                value={formData?.description || ''}
                onChange={(e) => onChange({ description: e.target.value })}
                rows={4}
                placeholder={t('shared.description_placeholder', 'Provide detailed information about the issue...')}
              />
              {errors.description && <small className="text-danger">{errors.description}</small>}
            </div>
          </Col>
        </Row>

        {/* Status field - only show in edit mode */}
        {isEditMode && (
          <Row className="mb-4">
            <Col md={6}>
              <div className="form-group">
                <label htmlFor="statuses" className="form-label fw-medium">{t('shared.status', 'Status')}</label>
                <Select
                  id="statuses"
                  options={[
                    { value: 'SUBMITTED', label: t('shared.open', 'Open') },
                    { value: 'IN_PROGRESS', label: t('shared.in_progress', 'In Progress') },
                    { value: 'ON_HOLD', label: t('shared.on_hold', 'On Hold') },
                    { value: 'RESOLVED', label: t('shared.resolved', 'Resolved') }
                  ]}
                  value={formData?.status ? {
                    value: formData.status,
                    label: { 'SUBMITTED': t('shared.open', 'Open'), 'IN_PROGRESS': t('shared.in_progress', 'In Progress'), 'ON_HOLD': t('shared.on_hold', 'On Hold'), 'RESOLVED': t('shared.resolved', 'Resolved') }[formData.status] || formData.status
                  } : null}
                  onChange={(selected) => onChange({ status: selected?.value || '' })}
                  isClearable
                  isSearchable
                  placeholder={t('shared.select_status', 'Select Status')}
                  classNamePrefix="react-select"
                  styles={selectStyles(!!errors.status)}
                />
                {errors.status && <small className="text-danger">{errors.status}</small>}
              </div>
            </Col>
          </Row>
        )}

        {/* Work Type and First Occurrence Row */}
        <Row className="mb-4 g-3">
          <Col md={6}>
            <div className="form-group">
              <label htmlFor="workType" className="form-label fw-medium">{t('shared.work_type', 'Work Type')}</label>
              <Select
                id="workType"
                options={[
                  { value: 'REACTIVE', label: t('shared.reactive', 'Reactive') },
                  { value: 'PREVENTIVE', label: t('shared.preventive', 'Preventive') },
                  { value: 'OTHER', label: t('shared.other', 'Other') }
                ]}
                value={{
                  value: formData?.workType || 'REACTIVE',
                  label: { 'REACTIVE': t('shared.reactive', 'Reactive'), 'PREVENTIVE': t('shared.preventive', 'Preventive'), 'OTHER': t('shared.other', 'Other') }[formData?.workType || 'REACTIVE']
                }}
                onChange={(selected) => onChange({ workType: selected?.value || 'REACTIVE' })}
                isSearchable
                placeholder={t('shared.select_work_type', 'Select Work Type')}
                classNamePrefix="react-select"
                styles={selectStyles(!!errors.workType)}
              />
              {errors.workType && <small className="text-danger">{errors.workType}</small>}
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label htmlFor="firstOccurrence" className="form-label fw-medium">{t('shared.first_occurrence', 'First Occurrence')}</label>
              <DatePicker
                id="firstOccurrence"
                selected={formData?.firstOccurrence ? new Date(formData.firstOccurrence) : null}
                onChange={(date) => {
                  onChange({ firstOccurrence: date ? formatDateTimeForAPI(date) : null });
                }}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="MMMM d, yyyy h:mm aa"
                className={`form-control ${errors.firstOccurrence ? 'is-invalid' : ''}`}
                wrapperClassName="w-100"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                placeholderText={t('shared.select_date_time', 'Select date and time')}
                isClearable
              />
              {errors.firstOccurrence && <div className="text-danger small mt-1">{errors.firstOccurrence}</div>}
            </div>
          </Col>
        </Row>

        {/* Recurrence Row */}
        <Row className="mb-4 g-3">
          <Col md={6}>
            <RecurrenceSelector
              recurrenceType={formData?.recurrenceType || 'NONE'}
              recurrenceDays={formData?.recurrenceDays || []}
              recurrenceInterval={formData?.recurrenceInterval || 1}
              recurrenceDayOfMonth={formData?.recurrenceDayOfMonth || 1}
              workType={formData?.workType || 'REACTIVE'}
              scheduledDate={formData?.requestDate}
              onChange={(changes) => onChange(changes)}
              errors={errors}
              showAlertInline={false}
            />
          </Col>

          {formData?.recurrenceType && formData.recurrenceType !== 'NONE' && (
            <Col md={6}>
              <div className="form-group">
                <label className="form-label fw-medium">{t('shared.recurrence_end_date', 'Recurrence End Date')}</label>
                <DatePicker
                  selected={formData?.recurrenceEndDate ? new Date(formData.recurrenceEndDate) : null}
                  onChange={(date) => {
                    onChange({ recurrenceEndDate: date ? formatDateTimeForAPI(date) : null });
                  }}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="MMMM d, yyyy h:mm aa"
                  placeholderText={t('shared.select_end_date_time', 'Select end date and time')}
                  className="form-control"
                  wrapperClassName="w-100"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  isClearable
                  minDate={formData?.firstOccurrence ? new Date(formData.firstOccurrence) : new Date()}
                />
              </div>
            </Col>
          )}
        </Row>

        {/* Preventive Info Banner */}
        {(formData?.workType === 'PREVENTIVE' && (!formData?.recurrenceType || formData.recurrenceType === 'NONE')) && (
          <Row className="mb-4">
            <Col md={12}>
              <div className="alert alert-info py-2 mb-0">
                <small>
                  <i className="mdi mdi-information-outline me-1"></i>
                  {t('shared.preventive_work_orders_are_usually_recur', 'Preventive work orders are usually recurring. Use the recurrence field to set a regular schedule.')}
                </small>
              </div>
            </Col>
          </Row>
        )}

        {/* Priority */}
        <Row className="mb-4 g-3">
          <Col md={6}>
            <div className="form-group">
              <label htmlFor="urgencyLevel" className="form-label fw-medium">{t('shared.priority', 'Priority')}</label>
              <Select
                id="urgencyLevel"
                options={enumData?.find(e => e.name === 'urgencyLevel')?.values?.map(val => ({
                  value: val,
                  label: t(`shared.${val.toLowerCase()}`, val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
                })) || []}
                value={formData?.urgencyLevel ? {
                  value: formData.urgencyLevel,
                  label: t(`shared.${formData.urgencyLevel.toLowerCase()}`, formData.urgencyLevel.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
                } : null}
                onChange={(selected) => onChange({ urgencyLevel: selected?.value || '' })}
                isClearable
                isSearchable
                placeholder={t('shared.select_priority', 'Select Priority')}
                classNamePrefix="react-select"
                styles={selectStyles(!!errors.urgencyLevel)}
              />
              {errors.urgencyLevel && <small className="text-danger">{errors.urgencyLevel}</small>}
            </div>
          </Col>
        </Row>

        {/* Due Date and Estimated Time */}
        <Row className="mb-4 g-3">
          <Col md={6}>
            <div className="form-group">
              <label htmlFor="requestDate" className="form-label fw-medium">{t('shared.due_date', 'Due Date')}</label>
              <DatePicker
                id="requestDate"
                selected={formData?.requestDate ? new Date(formData.requestDate) : null}
                onChange={(date) => {
                  onChange({ requestDate: date ? formatDateTimeForAPI(date) : null });
                }}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="MMMM d, yyyy h:mm aa"
                className={`form-control ${errors.requestDate ? 'is-invalid' : ''}`}
                wrapperClassName="w-100"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                placeholderText={t('shared.select_date_time', 'Select date and time')}
                isClearable
              />
              {errors.requestDate && <div className="text-danger small mt-1">{errors.requestDate}</div>}
            </div>
          </Col>
          <Col md={6}>
            <div className="form-group">
              <label htmlFor="estimatedTime" className="form-label fw-medium">{t('shared.estimated_time_hours', 'Estimated Time (Hours)')}</label>
              <input
                type="number"
                id="estimatedTime"
                className={`form-control ${errors.estimatedTime ? 'is-invalid' : ''}`}
                value={formData?.estimatedTime || ''}
                onChange={(e) => onChange({ estimatedTime: e.target.value })}
                placeholder={t('business.components.work_order_form.estimated_time_placeholder', 'Enter hours (e.g., 6 or 6.5)')}
                step="0.1"
                min="0"
              />
              {errors.estimatedTime && <small className="text-danger">{errors.estimatedTime}</small>}
            </div>
          </Col>
        </Row>

        {/* Assign to (users + teams) */}
        <Row className="mb-4 g-3">
          <Col md={12}>
            <div className="form-group">
              <label htmlFor="assignTo" className="form-label fw-medium">
                {t('workOrder.assignTo.label', 'Assign to')}
              </label>
              <WorkOrderAssignToPicker
                teams={teams || []}
                users={technicians || []}
                selectedTeamIds={(formData?.assignedTeams || []).map((tm) => tm.id || tm)}
                selectedUserIds={(formData?.assignedTechnician || []).map((u) => u.id || u)}
                onChange={({ teamIds, userIds }) => {
                  const teamLookup = new Map((teams || []).map((tm) => [tm.id, tm]));
                  const userLookup = new Map((technicians || []).map((u) => [u.id, u]));
                  onChange({
                    assignedTechnician: userIds.map((id) => userLookup.get(id) || { id }),
                    assignedTeams: teamIds.map((id) => teamLookup.get(id) || { id }),
                  });
                }}
                invalid={!!errors.assignedTechnician}
              />
              {errors.assignedTechnician && (
                <small className="text-danger d-block mt-1">{errors.assignedTechnician}</small>
              )}
            </div>
          </Col>
        </Row>

        {/* File/Document Upload */}
        <Row className="mb-4">
          <Col md={12}>
            <div className="form-group">
              <label className="form-label fw-medium">{t('shared.files', 'Files')}</label>
              <div
                className="border rounded p-4 text-center"
                style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: '#dee2e6', backgroundColor: '#fafbfc' }}
              >
                <input
                  type="file"
                  id="fileUploadInput"
                  className="d-none"
                  multiple
                  onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    if (files.length > 0) {
                      setFileUploading(true);
                      try {
                        const fd = new FormData();
                        files.forEach(file => fd.append('files', file));
                        const { data } = await api.post('files/pdf-upload', fd, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        const newUrls = (data.response || []).map(item => item.publicUrl);
                        onChange({ documentUrls: [...(formData?.documentUrls || []), ...newUrls] });
                      } catch (error) {
                        console.error('Error uploading files:', error);
                      } finally {
                        setFileUploading(false);
                      }
                    }
                  }}
                  disabled={fileUploading}
                />
                <label
                  htmlFor="fileUploadInput"
                  className={`btn btn-outline-secondary mb-2 ${fileUploading ? 'disabled' : ''}`}
                  style={{ cursor: fileUploading ? 'not-allowed' : 'pointer' }}
                >
                  {fileUploading ? (
                    <><Spinner size="sm" className="me-2" />{t('shared.uploading', 'Uploading...')}</>
                  ) : (
                    <><FeatherIcon icon="paperclip" size={16} className="me-2" />{t('shared.attach_files', 'Attach Files')}</>
                  )}
                </label>
                <p className="text-muted small mb-0">
                  {fileUploading ? t('shared.wait_uploading_files', 'Please wait while files are being uploaded...') : t('shared.click_to_select_files', 'Click to select files to attach')}
                </p>
              </div>
              {formData?.documentUrls?.length > 0 && (
                <div className="mt-3">
                  {formData.documentUrls.map((url, index) => (
                    <div key={index} className="d-flex align-items-center justify-content-between bg-light rounded px-3 py-2 mb-2">
                      <div className="d-flex align-items-center">
                        <FeatherIcon icon="file-text" size={18} className="text-primary me-2" />
                        <span className="text-truncate" style={{ maxWidth: '300px' }}>{url.split('/').pop()}</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => {
                          const newUrls = formData.documentUrls.filter((_, i) => i !== index);
                          onChange({ documentUrls: newUrls });
                        }}
                      >
                        <FeatherIcon icon="trash-2" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Categories and Vendors Multi-select */}
        <Row className="mb-4 g-3">
          <Col md={6}>
            <div className="form-group">
              <label className="form-label fw-medium">{t('shared.categories', 'Categories')}</label>
              <Select
                id="categories"
                isMulti
                options={categories
                  ?.sort((a, b) => a.name.localeCompare(b.name))
                  .map((cat) => ({
                    value: cat.id,
                    label: cat.name,
                    data: cat
                  })) || []}
                value={formData?.categoryIds?.map((catId) => {
                  const category = categories?.find(c => c.id === catId);
                  return {
                    value: catId,
                    label: category?.name || formData?.categoryNames?.[formData.categoryIds.indexOf(catId)] || `Category ${catId}`,
                    data: category
                  };
                }) || []}
                onChange={(selected) => {
                  onChange({
                    categoryIds: selected ? selected.map(s => s.value) : [],
                    categoryNames: selected ? selected.map(s => s.label) : []
                  });
                }}
                isClearable
                isSearchable
                placeholder={t('business.components.work_order_form.categories_placeholder', 'Search and select categories...')}
                classNamePrefix="react-select"
                styles={multiSelectStyles(false)}
                noOptionsMessage={() => t('shared.no_categories_found', 'No categories found')}
              />
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label fw-medium">{t('shared.vendors', 'Vendors')}</label>
              <Select
                id="vendors"
                isMulti
                options={vendors?.map((vendor) => ({
                  value: vendor.id,
                  label: vendor.vendorName,
                  data: vendor
                })) || []}
                value={formData?.vendorIds?.map((vendorId) => {
                  const vendor = vendors?.find(v => v.id === vendorId);
                  return {
                    value: vendorId,
                    label: vendor?.vendorName || formData?.vendorNames?.[formData.vendorIds.indexOf(vendorId)] || `Vendor ${vendorId}`,
                    data: vendor
                  };
                }) || []}
                onChange={(selected) => {
                  onChange({
                    vendorIds: selected ? selected.map(s => s.value) : [],
                    vendorNames: selected ? selected.map(s => s.label) : []
                  });
                }}
                isClearable
                isSearchable
                placeholder={t('business.components.work_order_form.vendors_placeholder', 'Search and select vendors...')}
                classNamePrefix="react-select"
                styles={multiSelectStyles(false)}
                noOptionsMessage={() => t('shared.no_vendors_found', 'No vendors found')}
              />
            </div>
          </Col>
        </Row>

        {apiError && (
          <div className="alert alert-danger mb-4">
            {apiError}
          </div>
        )}
      </form>

      {/* Asset Filter Modal */}
      <Modal
        isOpen={assetFilterModal}
        toggle={() => setAssetFilterModal(false)}
        centered
        className="fade-scale-modal"
        backdropClassName="modal-backdrop-blur"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header text-white">
            <h5 className="modal-title fs-4">{t('shared.filter_assets', 'Filter Assets')}</h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setAssetFilterModal(false)}
              aria-label={t('shared.close', 'Close')}
            ></button>
          </div>

          <div className="modal-body p-4">
            <Row className="mb-4 g-3">
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="assetFilterLocation" className="form-label fw-medium">{t('shared.location', 'Location')}</label>
                  <select
                    id="assetFilterLocation"
                    className="form-select"
                    value={assetFilters.location}
                    onChange={(e) => setAssetFilters({ ...assetFilters, location: e.target.value })}
                  >
                    <option value="">{t('shared.all_locations', 'All Locations')}</option>
                    {locations?.map((location) => (
                      <option key={location.id} value={location.locationName}>{location.locationName}</option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>

            <Row className="mb-4 g-3">
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="assetFilterManufacturer" className="form-label fw-medium">{t('shared.manufacturer', 'Manufacturer')}</label>
                  <select
                    id="assetFilterManufacturer"
                    className="form-select"
                    value={assetFilters.manufacturer}
                    onChange={(e) => setAssetFilters({ ...assetFilters, manufacturer: e.target.value })}
                  >
                    <option value="">{t('shared.all_manufacturers', 'All Manufacturers')}</option>
                    {manufacturers?.map((manufacturer) => (
                      <option key={manufacturer} value={manufacturer}>{manufacturer}</option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>
          </div>

          <div className="modal-footer bg-light border-top-0">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setAssetFilters({ location: '', manufacturer: '' });
                setAssetFilterModal(false);
              }}
            >
              {t('shared.clear_filters', 'Clear Filters')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setAssetFilterModal(false)}
            >
              {t('shared.apply_filters', 'Apply Filters')}
            </button>
          </div>
        </motion.div>
      </Modal>
    </>
  );
};

export default WorkOrderForm;
